/**
 * Shared log-redaction helpers for the Acequia stack.
 *
 * Console logs are not benign here: any peer's console is remotely
 * retrievable via a debug-console `console-readonly` grant, and server
 * stdout lands in PM2 logs. Never log a raw request/response/config/token
 * object — log a projection, or pass it through these helpers first.
 *
 * Standalone by design (zero imports) so server-side test runners can
 * import it without pulling browser deps. Seeded from BrowserDAV's
 * WebDAVFileSystem sanitizeRequestForLog / SENSITIVE_HEADER_RE.
 *
 * Deliberately aggressive: masking a non-secret that merely looks like a
 * JWT or key is acceptable; missing a real credential is not. Hex/base64
 * runs keep an 8-char prefix so identifiers (chain hashes, ids) stay
 * matchable for debugging.
 */

// Header / object-key names whose values are always masked wholesale.
export const SENSITIVE_HEADER_RE =
    /^(authorization|cookie|set-cookie|proxy-authorization|x-[\w-]*(token|key|secret|auth)[\w-]*)$/i

const SENSITIVE_KEY_RE =
    /^(authorization|cookie|set-cookie|proxy-authorization|password|secret|token|jwt|apitoken|api_key|apikey|access_token|auth_token|credential|credentials|private_key|privatekey)$/i

// Tier-0 value signatures scrubbed out of any logged string.
const REDACTIONS = [
    // JWTs — any eyJ… base64url run, plus its dotted segments
    { re: /eyJ[A-Za-z0-9_-]{8,}(?:\.[A-Za-z0-9_-]+){0,2}/g, sub: '«jwt-redacted»' },
    // Bearer <token>
    { re: /\bBearer\s+[A-Za-z0-9._~+/=-]{8,}/g, sub: 'Bearer «redacted»' },
    // auth_token=<value> (cookie / query string)
    { re: /\bauth_token=[^\s;&"']+/g, sub: 'auth_token=«redacted»' },
    // PEM private key blocks (END marker optional — truncated dumps still masked)
    {
        re: /-----BEGIN [A-Z ]*PRIVATE KEY-----[\s\S]*?(?:-----END [A-Z ]*PRIVATE KEY-----|$)/g,
        sub: '«private-key-redacted»',
    },
    // Long hex runs (40+): API tokens, key material. Keep an 8-char prefix.
    { re: /\b([a-fA-F0-9]{8})[a-fA-F0-9]{32,}\b/g, sub: '$1…«hex-redacted»' },
    // Long base64-ish runs (40+, no slashes — URLs stay readable). Keep prefix.
    { re: /\b([A-Za-z0-9+]{8})[A-Za-z0-9+]{32,}={0,2}(?![\w/])/g, sub: '$1…«b64-redacted»' },
]

function redactString(str) {
    if (str.length < 8) return str
    let out = str
    for (const { re, sub } of REDACTIONS) {
        out = out.replace(re, sub)
    }
    return out
}

/**
 * Redact sensitive material from a value before logging it.
 * Strings get value-signature scrubbing; objects/arrays are deep-copied
 * with sensitive-named keys masked and every string value scrubbed.
 * Non-string primitives pass through untouched.
 */
export function redactSensitive(value, _seen) {
    if (typeof value === 'string') return redactString(value)
    if (!value || typeof value !== 'object') return value

    const seen = _seen || new WeakSet()
    if (seen.has(value)) return '[Circular]'
    seen.add(value)

    if (Array.isArray(value)) return value.map(v => redactSensitive(v, seen))

    const out = {}
    for (const [key, val] of Object.entries(value)) {
        if (SENSITIVE_KEY_RE.test(key) || SENSITIVE_HEADER_RE.test(key)) {
            out[key] = val == null ? val : '«redacted»'
        } else {
            out[key] = redactSensitive(val, seen)
        }
    }
    return out
}

/**
 * Safe projection of a request-like object ({ method, path/url, headers, body }).
 * Headers matching SENSITIVE_HEADER_RE are masked; the body is summarized.
 */
export function sanitizeRequestForLog(request) {
    if (!request || typeof request !== 'object') return request
    const headers = {}
    for (const [name, val] of Object.entries(request.headers || {})) {
        headers[name] = SENSITIVE_HEADER_RE.test(name) ? '«redacted»' : redactSensitive(val)
    }
    const bodyBytes =
        typeof request.body === 'string'
            ? request.body.length
            : request.body?.byteLength ?? request.body?.size ?? null
    return {
        method: request.method,
        url: redactSensitive(request.url || request.path || ''),
        headers,
        body: request.body == null ? undefined : `«body omitted: ${bodyBytes ?? '?'} bytes»`,
    }
}

/**
 * Safe projection of a response-like object ({ status, headers, body }).
 */
export function sanitizeResponseForLog(response) {
    if (!response || typeof response !== 'object') return response
    const src = response.data && typeof response.data === 'object' ? response.data : response
    const headers = src.headers || {}
    const contentType =
        headers['Content-Type'] || headers['content-type'] || null
    const bodyBytes =
        typeof src.body === 'string'
            ? src.body.length
            : src.body?.byteLength ?? src.body?.size ?? null
    return {
        status: src.status ?? src.statusCode ?? null,
        contentType,
        bodyBytes,
        headerCount: Object.keys(headers).length,
    }
}
